"use client";

import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ListMusic, Plus } from "lucide-react";
import { toast } from "sonner";
import { useSiteAuthStore } from "@gerama/stores";
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, Input } from "@gerama/ui";
import { isApiError } from "@gerama/types";
import { browserCoreApi } from "@/lib/api";
import { openLogin } from "@/features/auth/login-modal";
import { VisibilityBadge } from "@/features/user-playlists/visibility";

const EVENT = "gerama:add-to-playlist";

interface PickerTarget {
  itemType: "song" | "video";
  itemId: number;
  name?: string;
}

/** Opens the "add to playlist" dialog for a song/video from anywhere (menus, the player) — no provider wiring needed. */
export function openPlaylistPicker(target: PickerTarget) {
  window.dispatchEvent(new CustomEvent<PickerTarget>(EVENT, { detail: target }));
}

/** Mounted once in the root layout; listens for `openPlaylistPicker`. */
export function PlaylistPickerDialog() {
  const token = useSiteAuthStore((s) => s.token);
  const client = useQueryClient();
  const [target, setTarget] = React.useState<PickerTarget | null>(null);
  const [name, setName] = React.useState("");

  React.useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<PickerTarget>).detail;
      if (!useSiteAuthStore.getState().token) {
        openLogin();
        return;
      }
      setName("");
      setTarget(detail);
    };
    window.addEventListener(EVENT, onOpen);
    return () => window.removeEventListener(EVENT, onOpen);
  }, []);

  const playlists = useQuery({
    queryKey: ["user-playlists", "mine"],
    queryFn: ({ signal }) => browserCoreApi.userPlaylists.mine({ limit: 100 }, signal),
    enabled: !!token && !!target,
  });

  const add = useMutation({
    mutationFn: async (playlistId: number) => {
      await browserCoreApi.userPlaylists.addItem(playlistId, target!.itemType, target!.itemId);
      return playlistId;
    },
    onSuccess: () => {
      toast.success("به پلی‌لیست اضافه شد");
      void client.invalidateQueries({ queryKey: ["user-playlists"] });
      setTarget(null);
    },
    onError: (error) => toast.error(isApiError(error) ? error.message : "افزودن ناموفق بود"),
  });

  const create = useMutation({
    mutationFn: async () => {
      const playlist = await browserCoreApi.userPlaylists.create(name.trim());
      await browserCoreApi.userPlaylists.addItem(playlist.id, target!.itemType, target!.itemId);
    },
    onSuccess: () => {
      toast.success("پلی‌لیست ساخته شد و آهنگ اضافه شد");
      void client.invalidateQueries({ queryKey: ["user-playlists"] });
      setTarget(null);
    },
    onError: (error) => toast.error(isApiError(error) ? error.message : "ساخت پلی‌لیست ناموفق بود"),
  });

  return (
    <Dialog open={!!target} onOpenChange={(open) => !open && setTarget(null)}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>افزودن به پلی‌لیست</DialogTitle>
        </DialogHeader>
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) create.mutate();
          }}
        >
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="پلی‌لیست جدید…"
            maxLength={120}
          />
          <Button
            type="submit"
            size="icon"
            disabled={!name.trim() || create.isPending}
            aria-label="ساخت"
          >
            <Plus />
          </Button>
        </form>
        <ul className="max-h-72 space-y-1 overflow-y-auto">
          {(playlists.data?.list ?? []).map((playlist) => (
            <li key={playlist.id}>
              <button
                type="button"
                disabled={add.isPending}
                onClick={() => add.mutate(playlist.id)}
                className="hover:bg-accent flex w-full items-center gap-3 rounded-md px-3 py-2 text-start text-sm"
              >
                <ListMusic className="text-muted-foreground size-4" />
                <span className="min-w-0 flex-1 truncate">{playlist.name}</span>
                <VisibilityBadge visibility={playlist.visibility} />
              </button>
            </li>
          ))}
          {playlists.data && playlists.data.list.length === 0 && (
            <li className="text-muted-foreground py-4 text-center text-sm">هنوز پلی‌لیستی نداری</li>
          )}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
